import { Theme } from '@core/enums';
import animation, { fadeInUp } from '@helpers/animation';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { legacyStylesOverride } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { ButtonStyles } from '@stories/Elements/Forms/Buttons/Button.styles';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import styled, { css } from 'styled-components';

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding-bottom: 51%;
  min-height: 140px;

  > img {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    transform: scale(1.12) translateX(10px);
    will-change: transform filter;
    transition: transform 300ms ${animation.easeInOutCubic}, filter 300ms ${animation.easeInOutCubic};

    @media ${from(Device.Tablet)} {
      min-height: 176px;
    }
  }
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 14px 18px;

  h3 {
    display: flex;
    align-items: center;
    flex: 1;
    margin: 0;
    color: ${brand.white};
    ${Typography.H6Style};
    text-align: center;

    ${legacyStylesOverride(`
      color: ${brand.white};
      ${Typography.H6Style};
    `)}
  }

  [data-text-only='true'] & {
    padding-top: ${fluid(16, 22)};
    padding-bottom: ${fluid(16, 22)};

    h3 {
      padding: 0 16px;

      @media ${until(Device.Tablet)} {
        padding: 0 24px;
        font-size: 14px;

        ${legacyStylesOverride(`
          font-size: 14px;
        `)}
      }
    }
  }
`;

const Card = styled.a<{ inView: boolean; delay: number; isCentered: boolean; theme: Theme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;

  ${({ theme }) => {
    switch (theme) {
      default:
      case Theme.Red: {
        return css`
          background-color: ${brand.red.base};
        `;
      }

      case Theme.White: {
        return css`
          background-color: ${brand.white};

          ${Text} h3 {
            color: ${brand.black};

            ${legacyStylesOverride(`
              color: ${brand.black};
            `)}
          }
        `;
      }

      case Theme.Black: {
        return css`
          background-color: ${brand.black};
        `;
      }
    }
  }};

  &[data-text-only='true'] {
    transition: background-color 0.2s ${animation.easeInOutCubic};

    &:hover,
    &:focus-visible {
      background-color: ${brand.grey[20]};
    }
  }

  ${({ inView, delay }) => (inView !== undefined ? fadeInUp(inView, 25, 500, delay, animation.easeInOutCubic) : null)};

  ${({ isCentered }) =>
    isCentered
      ? css`
          ${Text} {
            text-align: center;
          }
        `
      : null};

  &:hover {
    ${ImageWrapper} {
      &:before {
        transform: scaleX(1);
      }

      &:after {
        opacity: 1;
        transform: scale(1);
      }
    }

    ${ImageWrapper} > img {
      transform: scale(1.12) translateX(-10px);
      filter: grayscale(0.75) contrast(1.1);
    }
  }

  ${ButtonStyles.ButtonElement} {
    margin-top: 14px;
    font-size: 14px;

    @media ${from(Device.TabletLarge)} {
      font-size: 16px;
    }
  }
`;

const ButtonWrapper = styled.div`
  margin-top: auto;
`;

export const CategoryCardStyles = {
  Card,
  ImageWrapper,
  Text,
  ButtonWrapper,
};
